import React, { useState } from "react";

const CricketMatchDetails = ({ matches, userNames, styles }) => {
  // Get player name - handles both custom players and registered users
  const getPlayerName = (
    playerId,
    playerName,
    isCustom = false,
    teamPlayers = []
  ) => {
    // First check if we have a direct name from the stats
    if (playerName && playerName !== "Unknown" && playerName !== "Player") {
      return playerName;
    }

    // If it's a custom player, use the name from the data
    if (isCustom || !playerId || playerId === null) {
      return playerName || "Unknown Player";
    }

    // Try to find player in team players list
    const player = teamPlayers.find(
      (p) =>
        String(p.userId) === String(playerId) ||
        String(p.id) === String(playerId)
    );
    if (player?.name) {
      return player.name;
    }

    // Try to get from userNames mapping
    return userNames[playerId] || playerName || "Unknown Player";
  };

  const formatBallsToOvers = (balls) => {
    if (!balls && balls !== 0) return "0";
    const overs = Math.floor(balls / 6);
    const remainingBalls = balls % 6;
    return remainingBalls > 0 ? `${overs}.${remainingBalls}` : `${overs}`;
  };

  // Handle overs that might come as {overs, balls} or just as a number
  const formatOversDisplay = (innings) => {
    if (!innings) return "0";
    const overs = innings.overs || 0;
    const balls = innings.balls || 0;
    return balls > 0 ? `${overs}.${balls}` : `${overs}`;
  };

  const calculateStrikeRate = (runs, balls) => {
    if (!balls || balls === 0) return "0.00";
    return ((runs / balls) * 100).toFixed(2);
  };

  const calculateEconomyRate = (runs, overs, balls = 0) => {
    const totalOvers = (overs || 0) + (balls || 0) / 6;
    if (totalOvers === 0) return "0.00";
    return (runs / totalOvers).toFixed(2);
  };

  const calculateRunRate = (innings) => {
    if (!innings) return "0.00";
    const totalOvers = (innings.overs || 0) + (innings.balls || 0) / 6;
    if (totalOvers === 0) return "0.00";
    return ((innings.totalRuns || 0) / totalOvers).toFixed(2);
  };

  const getTotalExtras = (extras) => {
    if (!extras) return 0;
    return (
      (extras.byes || 0) +
      (extras.legByes || 0) +
      (extras.wides || 0) +
      (extras.noBalls || 0) +
      (extras.penalties || 0)
    );
  };

  // Function to determine batting order based on toss
  const getBattingOrder = (match) => {
    let firstBattingTeam, secondBattingTeam;

    if (match.tossWinner === "team1") {
      if (match.tossDecision === "bat") {
        firstBattingTeam = "team1";
        secondBattingTeam = "team2";
      } else {
        firstBattingTeam = "team2";
        secondBattingTeam = "team1";
      }
    } else if (match.tossWinner === "team2") {
      if (match.tossDecision === "bat") {
        firstBattingTeam = "team2";
        secondBattingTeam = "team1";
      } else {
        firstBattingTeam = "team1";
        secondBattingTeam = "team2";
      }
    } else {
      firstBattingTeam = "team1";
      secondBattingTeam = "team2";
    }

    return { firstBattingTeam, secondBattingTeam };
  };

  const getMatchTypeLabel = (matchType) => {
    switch (matchType) {
      case "t20":
        return "T20";
      case "odi":
        return "ODI";
      case "limited-overs":
        return "Limited Overs";
      default:
        return matchType || "Limited Overs";
    }
  };

  return matches.map((match, matchIndex) => {
    const { firstBattingTeam, secondBattingTeam } = getBattingOrder(match);
    const team1Name = match.team1Name || "Team 1";
    const team2Name = match.team2Name || "Team 2";

    const getTeamName = (team) => (team === "team1" ? team1Name : team2Name);
    const getTeamPlayers = (team) => match[`${team}Players`] || [];

    return (
      <div
        key={matchIndex}
        className="mb-8 bg-white rounded-xl shadow-lg overflow-hidden"
      >
        {/* Match Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div>
              <h4 className="text-lg sm:text-xl font-bold">
                Match #{matchIndex + 1}
              </h4>
              <p className="text-blue-100 text-sm mt-1">
                {team1Name} vs {team2Name}
              </p>
            </div>
            <span
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap ${
                match.winner === "team1"
                  ? "bg-green-500 text-white"
                  : match.winner === "team2"
                  ? "bg-green-500 text-white"
                  : match.winner === "draw"
                  ? "bg-gray-400 text-white"
                  : "bg-yellow-500 text-white"
              }`}
            >
              {match.winner === "team1"
                ? `${team1Name} Won`
                : match.winner === "team2"
                ? `${team2Name} Won`
                : match.winner === "draw"
                ? "Match Drawn"
                : "Result Pending"}
            </span>
          </div>

          {/* Match Info Grid */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 text-xs sm:text-sm">
            <div className="bg-white/10 rounded-lg p-2 sm:p-3">
              <span className="text-blue-200 block text-xs">Match Type</span>
              <span className="font-medium">
                {getMatchTypeLabel(match.matchType)}
              </span>
            </div>
            <div className="bg-white/10 rounded-lg p-2 sm:p-3">
              <span className="text-blue-200 block text-xs">Overs</span>
              <span className="font-medium">{match.totalOvers || 20}</span>
            </div>
            <div className="bg-white/10 rounded-lg p-2 sm:p-3 sm:col-span-1 col-span-1">
              <span className="text-blue-200 block text-xs">Toss</span>
              <span className="font-medium">
                {match.tossWinner === "team1" ? team1Name : team2Name} chose to{" "}
                {match.tossDecision === "bat" ? "bat" : "bowl"}
              </span>
            </div>
          </div>
        </div>

        {/* Match Result Banner */}
        {match.result && (
          <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-l-4 border-yellow-400 p-3 sm:p-4">
            <p className="text-yellow-800 font-semibold text-center text-sm sm:text-base">
              🏆 {match.result}
            </p>
          </div>
        )}

        {/* Score Summary Cards */}
        <div className="p-4 sm:p-6 bg-gray-50">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* First Batting Team */}
            <ScoreCard
              teamName={getTeamName(firstBattingTeam)}
              innings={match.innings?.[firstBattingTeam]}
              label="1st Innings"
              isWinner={match.winner === firstBattingTeam}
              calculateRunRate={calculateRunRate}
              formatOversDisplay={formatOversDisplay}
              getTotalExtras={getTotalExtras}
            />

            {/* Second Batting Team */}
            <ScoreCard
              teamName={getTeamName(secondBattingTeam)}
              innings={match.innings?.[secondBattingTeam]}
              label="2nd Innings"
              isWinner={match.winner === secondBattingTeam}
              calculateRunRate={calculateRunRate}
              formatOversDisplay={formatOversDisplay}
              getTotalExtras={getTotalExtras}
            />
          </div>
        </div>

        {/* Detailed Scorecard */}
        <div className="p-4 sm:p-6">
          <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 sm:mb-6 text-center flex items-center justify-center gap-2">
            <span>📊</span> Detailed Scorecard
          </h4>

          {/* First Innings Scorecard */}
          <InningsScorecard
            teamName={getTeamName(firstBattingTeam)}
            innings={match.innings?.[firstBattingTeam]}
            teamPlayers={getTeamPlayers(firstBattingTeam)}
            bowlingTeamPlayers={getTeamPlayers(secondBattingTeam)}
            label="1st Innings"
            colorClass="blue"
            getPlayerName={getPlayerName}
            calculateStrikeRate={calculateStrikeRate}
            calculateEconomyRate={calculateEconomyRate}
            formatBallsToOvers={formatBallsToOvers}
            getTotalExtras={getTotalExtras}
          />

          {/* Second Innings Scorecard */}
          <InningsScorecard
            teamName={getTeamName(secondBattingTeam)}
            innings={match.innings?.[secondBattingTeam]}
            teamPlayers={getTeamPlayers(secondBattingTeam)}
            bowlingTeamPlayers={getTeamPlayers(firstBattingTeam)}
            label="2nd Innings"
            colorClass="green"
            getPlayerName={getPlayerName}
            calculateStrikeRate={calculateStrikeRate}
            calculateEconomyRate={calculateEconomyRate}
            formatBallsToOvers={formatBallsToOvers}
            getTotalExtras={getTotalExtras}
          />

          {/* Ball by Ball Events (if available from real-time scoring) */}
          {match.ballEvents && match.ballEvents.length > 0 && (
            <BallByBallSection
              ballEvents={match.ballEvents}
              team1Name={team1Name}
              team2Name={team2Name}
              team1Players={match.team1Players || []}
              team2Players={match.team2Players || []}
              getPlayerName={getPlayerName}
            />
          )}

          {/* Teams Information */}
          <div className="mt-6 sm:mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <TeamPlayersList
              teamName={team1Name}
              players={match.team1Players || []}
              colorClass="blue"
              getPlayerName={getPlayerName}
            />
            <TeamPlayersList
              teamName={team2Name}
              players={match.team2Players || []}
              colorClass="green"
              getPlayerName={getPlayerName}
            />
          </div>
        </div>
      </div>
    );
  });
};

// Score Card Component
const ScoreCard = ({
  teamName,
  innings,
  label,
  isWinner,
  calculateRunRate,
  formatOversDisplay,
  getTotalExtras,
}) => {
  const totalRuns = innings?.totalRuns || 0;
  const wickets = innings?.wickets || 0;
  const overs = formatOversDisplay(innings);
  const runRate = calculateRunRate(innings);
  const extras = getTotalExtras(innings?.extras);

  return (
    <div
      className={`bg-white rounded-xl p-4 sm:p-5 border-2 ${
        isWinner ? "border-green-400 shadow-green-100" : "border-gray-200"
      } shadow-md transition-all hover:shadow-lg`}
    >
      <div className="flex items-center justify-between mb-3">
        <h5 className="text-base sm:text-lg font-bold text-gray-800 truncate pr-2">
          {teamName}
        </h5>
        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full whitespace-nowrap">
          {label}
        </span>
      </div>

      <div className="text-center mb-3">
        <div className="text-3xl sm:text-4xl font-bold text-gray-900">
          {totalRuns}
          <span className="text-gray-400">/</span>
          <span className="text-red-500">{wickets}</span>
        </div>
        <div className="text-sm text-gray-500 mt-1">({overs} overs)</div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-center text-xs sm:text-sm">
        <div className="bg-blue-50 rounded-lg p-2">
          <div className="text-blue-600 font-semibold">{runRate}</div>
          <div className="text-gray-500">Run Rate</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-2">
          <div className="text-purple-600 font-semibold">{extras}</div>
          <div className="text-gray-500">Extras</div>
        </div>
      </div>

      {isWinner && (
        <div className="mt-3 text-center">
          <span className="inline-flex items-center gap-1 text-green-600 font-semibold text-sm">
            🏆 Winner
          </span>
        </div>
      )}
    </div>
  );
};

// Innings Scorecard Component
const InningsScorecard = ({
  teamName,
  innings,
  teamPlayers,
  bowlingTeamPlayers,
  label,
  colorClass,
  getPlayerName,
  calculateStrikeRate,
  calculateEconomyRate,
  formatBallsToOvers,
  getTotalExtras,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  if (!innings) return null;

  const borderColor =
    colorClass === "blue" ? "border-blue-500" : "border-green-500";
  const textColor = colorClass === "blue" ? "text-blue-600" : "text-green-600";

  return (
    <div className="mb-6 sm:mb-8">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full text-left font-bold ${textColor} mb-4 border-b-2 ${borderColor} pb-2 flex items-center justify-between hover:opacity-80 transition-opacity`}
      >
        <span className="text-base sm:text-lg">
          {teamName} - {label}
        </span>
        <span className="text-xl">{isExpanded ? "▼" : "▶"}</span>
      </button>

      {isExpanded && (
        <div className="space-y-4 sm:space-y-6">
          {/* Batting Table */}
          {innings.battingStats && innings.battingStats.length > 0 && (
            <div>
              <h6 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <span>🏏</span> Batting
              </h6>
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <div className="min-w-full inline-block align-middle px-4 sm:px-0">
                  <table className="w-full border border-gray-200 rounded-lg overflow-hidden text-xs sm:text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-left font-semibold text-gray-700 min-w-[120px]">
                          Batsman
                        </th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-center font-semibold text-gray-700">
                          R
                        </th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-center font-semibold text-gray-700">
                          B
                        </th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-center font-semibold text-gray-700 hidden sm:table-cell">
                          4s
                        </th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-center font-semibold text-gray-700 hidden sm:table-cell">
                          6s
                        </th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-center font-semibold text-gray-700">
                          SR
                        </th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-center font-semibold text-gray-700 hidden md:table-cell">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {innings.battingStats.map((batsman, index) => {
                        const playerInfo = teamPlayers.find(
                          (p) =>
                            String(p.userId) === String(batsman.playerId) ||
                            String(p.id) === String(batsman.playerId)
                        );

                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-2 sm:px-4 py-2 sm:py-3">
                              <div className="font-medium text-gray-900">
                                {getPlayerName(
                                  batsman.playerId,
                                  batsman.playerName,
                                  playerInfo?.isCustom,
                                  teamPlayers
                                )}
                              </div>
                              {batsman.isOut && batsman.howOut && (
                                <div className="text-xs text-gray-500 mt-0.5 hidden md:block">
                                  {batsman.howOut}
                                  {batsman.bowlerOut &&
                                    ` (${batsman.bowlerOut})`}
                                </div>
                              )}
                            </td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 text-center font-bold text-gray-900">
                              {batsman.runs || 0}
                            </td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 text-center text-gray-600">
                              {batsman.balls || 0}
                            </td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 text-center text-gray-600 hidden sm:table-cell">
                              {batsman.fours || 0}
                            </td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 text-center text-gray-600 hidden sm:table-cell">
                              {batsman.sixes || 0}
                            </td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 text-center text-gray-600">
                              {calculateStrikeRate(batsman.runs, batsman.balls)}
                            </td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 text-center hidden md:table-cell">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  batsman.isOut
                                    ? "bg-red-100 text-red-700"
                                    : "bg-green-100 text-green-700"
                                }`}
                              >
                                {batsman.isOut ? "Out" : "Not Out"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Bowling Table */}
          {innings.bowlingStats && innings.bowlingStats.length > 0 && (
            <div>
              <h6 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <span>⚾</span> Bowling
              </h6>
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <div className="min-w-full inline-block align-middle px-4 sm:px-0">
                  <table className="w-full border border-gray-200 rounded-lg overflow-hidden text-xs sm:text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-left font-semibold text-gray-700 min-w-[100px]">
                          Bowler
                        </th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-center font-semibold text-gray-700">
                          O
                        </th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-center font-semibold text-gray-700 hidden sm:table-cell">
                          M
                        </th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-center font-semibold text-gray-700">
                          R
                        </th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-center font-semibold text-gray-700">
                          W
                        </th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-center font-semibold text-gray-700">
                          Econ
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {innings.bowlingStats.map((bowler, index) => {
                        const playerInfo = bowlingTeamPlayers.find(
                          (p) =>
                            String(p.userId) === String(bowler.playerId) ||
                            String(p.id) === String(bowler.playerId)
                        );

                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-2 sm:px-4 py-2 sm:py-3 font-medium text-gray-900">
                              {getPlayerName(
                                bowler.playerId,
                                bowler.playerName,
                                playerInfo?.isCustom,
                                bowlingTeamPlayers
                              )}
                            </td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 text-center text-gray-600">
                              {formatBallsToOvers(
                                (bowler.overs || 0) * 6 + (bowler.balls || 0)
                              )}
                            </td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 text-center text-gray-600 hidden sm:table-cell">
                              {bowler.maidens || 0}
                            </td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 text-center text-gray-600">
                              {bowler.runs || 0}
                            </td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 text-center font-bold text-gray-900">
                              {bowler.wickets || 0}
                            </td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 text-center text-gray-600">
                              {bowler.economy ||
                                calculateEconomyRate(
                                  bowler.runs,
                                  bowler.overs,
                                  bowler.balls
                                )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Extras Breakdown */}
          {innings.extras && (
            <div>
              <h6 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <span>📊</span> Extras: {getTotalExtras(innings.extras)}
              </h6>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-3">
                <ExtrasItem label="Wides" value={innings.extras.wides || 0} />
                <ExtrasItem
                  label="No Balls"
                  value={innings.extras.noBalls || 0}
                />
                <ExtrasItem label="Byes" value={innings.extras.byes || 0} />
                <ExtrasItem
                  label="Leg Byes"
                  value={innings.extras.legByes || 0}
                />
                <ExtrasItem
                  label="Penalties"
                  value={innings.extras.penalties || 0}
                />
              </div>
            </div>
          )}

          {/* Fall of Wickets */}
          {innings.fallOfWickets && innings.fallOfWickets.length > 0 && (
            <div>
              <h6 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <span>📉</span> Fall of Wickets
              </h6>
              <div className="flex flex-wrap gap-2">
                {innings.fallOfWickets.map((fow, index) => (
                  <span
                    key={index}
                    className="bg-red-50 text-red-700 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm"
                  >
                    {fow.score}/{fow.wicketNumber} (
                    {fow.playerName || "Batsman"}, {fow.overs} ov)
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Extras Item Component
const ExtrasItem = ({ label, value }) => (
  <div className="bg-gray-50 rounded-lg p-2 sm:p-3 text-center">
    <div className="text-base sm:text-lg font-bold text-gray-800">{value}</div>
    <div className="text-xs text-gray-500">{label}</div>
  </div>
);

// Ball by Ball Section Component
const BallByBallSection = ({
  ballEvents,
  team1Name,
  team2Name,
  team1Players,
  team2Players,
  getPlayerName,
}) => {
  const [showBallEvents, setShowBallEvents] = useState(false);

  const getInningsName = (innings) =>
    innings === "team1" ? team1Name : team2Name;
  const getPlayers = (innings) =>
    innings === "team1" ? team1Players : team2Players;
  const getBowlingPlayers = (innings) =>
    innings === "team1" ? team2Players : team1Players;

  const resolveName = (playerId, players) => {
    const player = players.find(
      (p) =>
        String(p.userId) === String(playerId) ||
        String(p.id) === String(playerId)
    );
    return player?.name || "Player";
  };

  // Group balls by innings and over
  const groupedEvents = {};
  ballEvents.forEach((ball, index) => {
    const inningsKey = ball.innings || "team1";
    if (!groupedEvents[inningsKey]) {
      groupedEvents[inningsKey] = {};
    }

    // Calculate over number (based on legal deliveries before this ball)
    const prevLegalBalls = ballEvents
      .slice(0, index)
      .filter(
        (b) => b.innings === ball.innings && b.isLegalDelivery !== false
      ).length;
    const overNumber = Math.floor(prevLegalBalls / 6) + 1;

    if (!groupedEvents[inningsKey][overNumber]) {
      groupedEvents[inningsKey][overNumber] = [];
    }
    groupedEvents[inningsKey][overNumber].push(ball);
  });

  const getBallDisplay = (ball) => {
    const runs = ball.runsOffBat || 0;
    const extras = ball.extraRuns || 0;
    const extraType = ball.extraType || "none";

    if (ball.wicket?.isWicket) {
      return (
        <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
          W
        </span>
      );
    }

    if (extraType === "wide") {
      return (
        <span className="bg-yellow-400 text-yellow-800 px-2 py-1 rounded-full text-xs font-bold">
          Wd{extras > 1 ? `+${extras - 1}` : ""}
        </span>
      );
    }

    if (extraType === "no-ball") {
      return (
        <span className="bg-orange-400 text-orange-800 px-2 py-1 rounded-full text-xs font-bold">
          NB{runs > 0 ? `+${runs}` : ""}
        </span>
      );
    }

    if (runs === 4) {
      return (
        <span className="bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-bold">
          4
        </span>
      );
    }

    if (runs === 6) {
      return (
        <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-bold">
          6
        </span>
      );
    }

    if (extraType === "bye" || extraType === "leg-bye") {
      return (
        <span className="bg-gray-300 text-gray-700 px-2 py-1 rounded-full text-xs">
          {extras}
          {extraType === "bye" ? "b" : "lb"}
        </span>
      );
    }

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs ${
          runs === 0 ? "bg-gray-200 text-gray-600" : "bg-gray-100 text-gray-800"
        }`}
      >
        {runs}
      </span>
    );
  };

  return (
    <div className="mt-6 sm:mt-8">
      <button
        onClick={() => setShowBallEvents(!showBallEvents)}
        className="w-full text-left font-bold text-purple-600 mb-4 border-b-2 border-purple-500 pb-2 flex items-center justify-between hover:opacity-80 transition-opacity"
      >
        <span className="text-base sm:text-lg flex items-center gap-2">
          <span>🎯</span> Ball-by-Ball Commentary
        </span>
        <span className="text-xl">{showBallEvents ? "▼" : "▶"}</span>
      </button>

      {showBallEvents && (
        <div className="space-y-6">
          {Object.entries(groupedEvents).map(([inningsKey, overs]) => (
            <div key={inningsKey}>
              <h6 className="font-semibold text-gray-700 mb-3 bg-gray-100 p-2 rounded">
                {getInningsName(inningsKey)} Innings
              </h6>
              <div className="space-y-3">
                {Object.entries(overs).map(([overNum, balls]) => (
                  <div
                    key={overNum}
                    className="flex flex-wrap items-start gap-2 p-2 bg-gray-50 rounded-lg"
                  >
                    <span className="font-semibold text-gray-600 text-sm min-w-[60px]">
                      Over {overNum}:
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {balls.map((ball, idx) => (
                        <div
                          key={idx}
                          className="cursor-default"
                          title={`${resolveName(
                            ball.strikerId,
                            getPlayers(ball.innings)
                          )} vs ${resolveName(
                            ball.bowlerId,
                            getBowlingPlayers(ball.innings)
                          )}`}
                        >
                          {getBallDisplay(ball)}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Team Players List Component
const TeamPlayersList = ({ teamName, players, colorClass, getPlayerName }) => {
  if (!players || players.length === 0) return null;

  const bgColor = colorClass === "blue" ? "bg-blue-50" : "bg-green-50";
  const textColor = colorClass === "blue" ? "text-blue-800" : "text-green-800";

  return (
    <div className={`${bgColor} rounded-xl p-4`}>
      <h6 className={`font-bold ${textColor} mb-3 text-sm sm:text-base`}>
        {teamName} Squad ({players.length})
      </h6>
      <div className="grid grid-cols-1 gap-2">
        {players.map((player, index) => (
          <div
            key={player.id || index}
            className="flex items-center justify-between bg-white rounded-lg p-2 shadow-sm"
          >
            <span className="text-xs sm:text-sm text-gray-700">
              <span className="font-medium text-gray-400 mr-2">
                {index + 1}.
              </span>
              {getPlayerName(
                player.userId,
                player.name,
                player.isCustom,
                players
              )}
            </span>
            {player.isCustom && (
              <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                Guest
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CricketMatchDetails;
